<template>
  <BaseWidget 
    title="Time saved by automations"
    :show-info-icon="true"
    info-tooltip="Estimated time saved by your automations within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/articles/9500741-what-is-an-automation', text: 'Learn about Automations' }"
    :automations-count="false"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="rowSpan >= 2"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <MetricValue 
        :value="timeSaved" 
        :subtitle="dateRangeLabel"
        :has-data-table="rowSpan >= 2 && enrollments.length > 0"
      >
        <template #comparison>
          <ComparisonChip 
            :change="changePercentage" 
            :previous-value="previousTimeSaved"
            :previous-period-dates="previousTimestamps"
            :date-range-preset="dateRange"
            label="Time saved by automations"
          />
        </template>
        <template
          v-if="hasMore"
          #subtitle-suffix
        >
          <span
            class="focused-view-all-link"
            @click="openViewAllPanel"
          >View all</span>
        </template>
      </MetricValue>
      
      <div
        v-if="!focused && rowSpan >= 2 && enrollments.length > 0"
        ref="tableContainerRef"
        class="enrollments-table-outer"
      >
        <div
          class="enrollments-table"
          :style="{ '--table-row-height': tableRowHeightPx + 'px' }"
        >
          <table>
            <thead>
              <tr>
                <th>Automation</th>
                <th class="text-right">
                  Time Saved
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(enrollment, index) in visibleEnrollments"
                :key="index"
                :class="{ 'clickable': enrollment.workflowId }"
                @click="enrollment.workflowId ? openWorkflowGuarded(enrollment.workflowId) : null"
              >
                <td>
                  <span class="enrollment-name-cell">
                    <span class="enrollment-name-text">{{ enrollment.name }}</span>
                    <span
                      v-if="enrollment.isDefault"
                      class="default-chip"
                    >Default</span>
                  </span>
                </td>
                <td class="text-right">
                  {{ formatMinutes(enrollment.minutesSaved) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <template #footer>
      <button
        v-if="!loading && !error && !editMode"
        type="button"
        :class="['ts-footer-bar', { 'ts-footer-bar--suggestion': totalMinutesSaved === 0 }]"
        data-automations-footer
        @click.stop="handleFooterClick"
      >
        <span
          class="ts-footer-icon-badge"
          :class="totalMinutesSaved > 0 ? 'ts-footer-icon-badge--green' : 'ts-footer-icon-badge--yellow'"
        >
          <span class="material-icons-outlined">bolt</span>
        </span>
        <span class="ts-footer-text">{{ footerText }}</span>
        <span class="material-icons-outlined ts-footer-chevron">chevron_right</span>
      </button>
    </template>
  </BaseWidget>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useTimeSavedData } from '../composables/useWidgetData.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'
import { DATE_RANGE_LABELS, getPreviousPeriodTimestamps } from '../utils/dateRanges.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'last_7_days')

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Last 7 days'
})

// Get previous period timestamps for tooltip
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value)
})

const { data, loading, error, cleanup, refresh } = useTimeSavedData({
  variables: computed(() => ({ dateRange: dateRange.value })),
  priority: props.priority ?? 1
})

// Fetch previous period data for comparison
const {
  data: previousData,
  loading: previousLoading,
  error: previousError,
  cleanup: previousCleanup
} = useTimeSavedData({
  dataKey: 'timeSaved_previous',
  isPreviousPeriod: true,
  variables: computed(() => ({ dateRange: dateRange.value })),
  priority: props.priority ?? 1
})

// Get enrollments from data
const enrollments = computed(() => {
  if (!data.value?.enrollments) return []
  return data.value.enrollments
})

// Open "View all" in native Optix side panel
const openViewAllPanel = () => {
  pushAdminSidePanel('view-all-time-saved')
}

// Footer: count of workflows that saved time in the period
const activeWorkflowCount = computed(() => {
  return enrollments.value.filter(e => e.minutesSaved > 0).length
})

const footerText = computed(() => {
  if (totalMinutesSaved.value === 0) {
    return 'Set up automations to start saving time'
  }
  const n = activeWorkflowCount.value
  return `${n} workflow${n === 1 ? '' : 's'} automated`
})

const handleFooterClick = () => {
  if (totalMinutesSaved.value === 0) {
    pushAdminSidePanel('view-all-best-practices')
  } else {
    openViewAllPanel()
  }
}

// Table sizing: show only as many rows as fit (no scroll)
const ROW_HEIGHT_PX = 56
const TABLE_HEADER_HEIGHT_PX = 40
const TARGET_ROWS_WHEN_3_TALL = 4
const tableContainerRef = ref(null)
const maxVisibleRows = ref(5)
const availableHeightRef = ref(0)

const hasMore = computed(() => enrollments.value.length > maxVisibleRows.value)

const visibleEnrollments = computed(() => {
  return enrollments.value.slice(0, maxVisibleRows.value)
})

// When widget is 3 rows tall, use flexible row height so 4 rows fit (min 56px)
const tableRowHeightPx = computed(() => {
  if (props.rowSpan >= 3 && availableHeightRef.value > 0) {
    const forRows = availableHeightRef.value - TABLE_HEADER_HEIGHT_PX
    const calculated = Math.max(32, forRows / TARGET_ROWS_WHEN_3_TALL)
    return Math.max(ROW_HEIGHT_PX, calculated)
  }
  return ROW_HEIGHT_PX
})

function updateMaxVisibleRows() {
  const el = tableContainerRef.value
  if (!el) return
  const availableHeight = el.clientHeight
  availableHeightRef.value = availableHeight
  if (availableHeight <= 0) return
  if (props.rowSpan >= 3) {
    maxVisibleRows.value = TARGET_ROWS_WHEN_3_TALL
    return
  }
  const rowsThatFit = Math.max(1, Math.floor((availableHeight - TABLE_HEADER_HEIGHT_PX) / ROW_HEIGHT_PX))
  maxVisibleRows.value = rowsThatFit
}

// Calculate total time saved in minutes
const totalMinutesSaved = computed(() => {
  if (!enrollments.value || enrollments.value.length === 0) return 0
  return enrollments.value.reduce((total, enrollment) => {
    return total + (enrollment.minutesSaved || 0)
  }, 0)
})

const HOURS_PER_WORK_DAY = 8

function formatTimeSavedDisplay(totalMinutes) {
  if (totalMinutes === 0) return '0 hours'
  
  const hours = totalMinutes / 60
  if (hours >= HOURS_PER_WORK_DAY) {
    const workDays = hours / HOURS_PER_WORK_DAY
    const formatted = workDays % 1 === 0 ? workDays.toFixed(0) : workDays.toFixed(1)
    return `${formatted} work day${formatted === '1' ? '' : 's'}`
  }
  
  if (hours % 1 === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }
  const formatted = hours.toFixed(1)
  return `${formatted} hours`
}

// Format total time saved - show work days when >= 8 hours, otherwise hours
const timeSaved = computed(() => formatTimeSavedDisplay(totalMinutesSaved.value))

// Format previous period time saved
const previousTimeSaved = computed(() => {
  if (previousData.value === null || previousData.value === undefined) return null
  
  const previousMinutes = previousData.value?.enrollments 
    ? previousData.value.enrollments.reduce((total, enrollment) => {
        return total + (enrollment.minutesSaved || 0)
      }, 0)
    : 0
  
  if (previousMinutes === 0) return null
  
  return formatTimeSavedDisplay(previousMinutes)
})

const changePercentage = computed(() => {
  // Don't show comparison while data is loading
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previousData.value === null || previousData.value === undefined) {
    return null
  }
  
  const current = totalMinutesSaved.value
  const previous = previousData.value?.enrollments 
    ? previousData.value.enrollments.reduce((total, enrollment) => {
        return total + (enrollment.minutesSaved || 0)
      }, 0)
    : 0
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100
  }
  
  // Calculate percentage change
  const change = ((current - previous) / previous) * 100
  return Math.round(change * 10) / 10 // Round to 1 decimal place
})

const formatMinutes = (minutes) => {
  if (!minutes) return '0 min'
  if (minutes >= 60) {
    const hours = minutes / 60
    const formatted = hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)
    return `${formatted} hour${formatted === '1' ? '' : 's'}`
  }
  return `${minutes} min`
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  let clientId = ''
  let canvasUUID = ''
  
  // First try current URL
  const urlParams = new URLSearchParams(window.location.search)
  clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  
  // Try referrer if not found
  if (!clientId || !canvasUUID) {
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer)
        const referrerParams = new URLSearchParams(referrerUrl.search)
        if (!clientId) {
          clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
        }
      } catch (e) {
        // Ignore
      }
    }
  }
  
  // Try parent window URL if accessible (may fail due to CORS)
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentUrl = window.parent.location.href
        const parentUrlObj = new URL(parentUrl)
        const parentParams = new URLSearchParams(parentUrlObj.search)
        if (!clientId) {
          clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
        }
      }
    } catch (e) {
      // CORS error - parent window is on different domain, ignore
    }
  }
  
  return { clientId, canvasUUID }
}

// Open workflow view when row is clicked
const { guard } = useTrialModeGuard()
const openWorkflowGuarded = guard((workflowId) => {
  if (!workflowId) {
    console.warn('⚠️ Cannot open workflow: workflowId is missing')
    return
  }
  
  const params = getUrlParams()
  
  // Generate canvasUUID in Optix format if not found: {timestamp}-{random}
  // Format: 1770326262653-544527 (13-digit timestamp + dash + 6-digit random)
  const generateCanvasUUID = () => {
    const timestamp = Date.now()
    const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0')
    return `${timestamp}-${random}`
  }
  
  const finalCanvasUUID = params.canvasUUID || generateCanvasUUID()
  
  // Send WorkflowView message to parent window
  const directMessage = {
    workflowId: workflowId.toString(),
    canvasUUID: finalCanvasUUID,
    client_id: params.clientId || undefined
  }
  
  // Also try command format
  const commandMessage = {
    command: 'WorkflowView',
    payload: directMessage
  }
  
  try {
    if (window.parent && window.parent !== window) {
      // Send direct format first
      window.parent.postMessage(directMessage, "*")
      
      // Also send command format
      setTimeout(() => {
        window.parent.postMessage(commandMessage, "*")
      }, 50)
      
      // Also try window.top in case of nested iframes
      if (window.top && window.top !== window && window.top !== window.parent) {
        window.top.postMessage(directMessage, "*")
        setTimeout(() => {
          window.top.postMessage(commandMessage, "*")
        }, 50)
      }
    }
  } catch (error) {
    console.error('❌ Error posting WorkflowView message:', error)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

// When enrollments load and table mounts, measure and update visible row count, and start observing
watch(
  () => enrollments.value.length,
  async () => {
    if (enrollments.value.length > 0) {
      await nextTick()
      updateMaxVisibleRows()
      startObserving()
    }
  }
)

let resizeObserver = null
let observedElement = null

function startObserving() {
  const el = tableContainerRef.value
  if (!el || !resizeObserver || el === observedElement) return
  resizeObserver.observe(el)
  observedElement = el
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => updateMaxVisibleRows())
  }
  const el = tableContainerRef.value
  if (el) {
    updateMaxVisibleRows()
    startObserving()
  }
})

onUnmounted(() => {
  if (resizeObserver && observedElement) {
    resizeObserver.unobserve(observedElement)
    observedElement = null
  }
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
}


.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.enrollments-table-outer {
  flex: 1;
  min-height: 0;
  margin-top: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: hidden;
}

.enrollments-table {
  margin-top: 0;
  padding: 0;
}

.enrollments-table table {
  width: 100%;
  min-width: 300px;
  border-collapse: collapse;
  table-layout: fixed;
}

.enrollments-table thead {
  background: white;
}

.enrollments-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 8px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.enrollments-table th.text-right {
  text-align: right;
  width: 90px;
  white-space: nowrap;
}

.enrollments-table tbody tr {
  height: var(--table-row-height, 56px);
  min-height: 32px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.enrollments-table tbody tr.clickable {
  cursor: pointer;
}

.enrollments-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.enrollments-table tbody tr:last-child {
  border-bottom: none;
}

.enrollments-table td {
  padding: 12px 8px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
}

.enrollments-table td:first-child {
  max-width: 230px;
  overflow: hidden;
}

.enrollment-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.enrollment-name-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.default-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
}

.enrollments-table td.text-right {
  text-align: right;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

.view-all-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 32px;
  padding-top: 4px;
  flex-shrink: 0;
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 400;
  cursor: pointer;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

/* Automations footer */
.ts-footer-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(100% + 40px);
  margin: 0 -20px -10px -20px;
  min-height: 48px;
  padding: 8px 20px;
  border: none;
  border-radius: 0 0 12px 12px;
  background: rgba(255, 255, 255, 1);
  color: rgba(0, 0, 0, 0.6);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  text-align: left;
  position: relative;
  z-index: 1;
}

.ts-footer-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 20px;
  right: 20px;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  transition: left 0.2s ease, right 0.2s ease;
}

.ts-footer-bar:hover::before {
  left: 0;
  right: 0;
}

.ts-footer-bar:hover {
  background: #e9ebee;
}

.ts-footer-bar--suggestion {
  background: white;
  color: var(--color-yellow-text);
}

.ts-footer-bar--suggestion:hover {
  background: #fffbeb;
}

.ts-footer-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
}

.ts-footer-icon-badge .material-icons-outlined {
  font-size: 14px;
}

.ts-footer-icon-badge--green {
  background: var(--color-bg-green);
}

.ts-footer-icon-badge--green .material-icons-outlined {
  color: #1C3829;
}

.ts-footer-icon-badge--yellow {
  background: var(--color-bg-yellow);
}

.ts-footer-icon-badge--yellow .material-icons-outlined {
  color: var(--color-yellow-text);
}

.ts-footer-bar--suggestion .ts-footer-text {
  color: rgba(0, 0, 0, 0.68);
}

.ts-footer-text {
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.68);
  padding-top: 2px;
  line-height: 1.4;
}

.ts-footer-chevron {
  flex-shrink: 0;
  font-size: 18px;
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
  color: inherit;
}

.ts-footer-bar:hover .ts-footer-chevron {
  opacity: 1;
  transform: translateX(0);
}
</style>
